import { Table, Text, Group } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import { groupAverageText } from '../../../utils/columnAverages';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';

interface CollapsibleGroupHeaderProps {
  label: string;
  count: number;
  colSpan: number;
  isCollapsed: boolean;
  onToggle: () => void;
  /** Optional label after the count, e.g. "sessions" → "(5 sessions)" */
  entityLabel?: string;
  /** MDF-39: nesting level, 0 = outermost. Inner levels are indented and do not stick. */
  depth?: number;
  /** MED-1308: the visible columns and the group's rows; numeric columns then show the group's average */
  columns?: ColumnConfig[];
  rows?: unknown[];
  /** A row's raw value for a column, as the top averages row reads it */
  getValue?: (row: never, columnId: string) => unknown;
}

const defaultGetValue = (row: unknown, columnId: string) => (row as Record<string, unknown>)[columnId];

export function CollapsibleGroupHeader({
  label,
  count,
  colSpan,
  isCollapsed,
  onToggle,
  entityLabel,
  depth = 0,
  columns,
  rows,
  getValue = defaultGetValue,
}: CollapsibleGroupHeaderProps) {
  const background = depth === 0 ? '#f0eeea' : '#f7f6f3';
  // With averages, the label sits in the first column and pins left like it, and each column after it gets its own cell.
  const firstNumeric = rows ? (columns?.findIndex((col, index) => index > 0 && col.numeric) ?? -1) : -1;
  const split = firstNumeric > 0;
  const read = getValue as (row: unknown, columnId: string) => unknown;
  const stickyTop = depth === 0 ? classes.stickyGroupHeader : undefined;
  return (
    <Table.Tr>
      <Table.Td
        colSpan={split ? 1 : colSpan}
        className={split ? `${stickyTop ?? ''} ${classes.stickyGroupLabel}` : stickyTop}
        style={{
          backgroundColor: background,
          fontWeight: 600,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          padding: `6px 16px 6px ${16 + depth * 24}px`,
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <div
          className={split ? classes.groupLabelOverflow : classes.stickyGroupHeaderContent}
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          <Group gap={6} wrap={split ? 'nowrap' : undefined} style={split ? { display: 'inline-flex', backgroundColor: background, paddingRight: 8 } : undefined}>
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: 0.3 }}>
              {label}
            </Text>
            <Text size="xs" c="dimmed">
              ({count}{entityLabel ? ` ${entityLabel}` : ''})
            </Text>
          </Group>
        </div>
      </Table.Td>
      {firstNumeric > 1 && <Table.Td colSpan={firstNumeric - 1} className={stickyTop} style={{ backgroundColor: background }} />}
      {split &&
        columns!.slice(firstNumeric).map(col => (
          <Table.Td key={col.id} className={stickyTop} style={{ backgroundColor: background, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {col.numeric ? groupAverageText(rows!.map(row => read(row, col.id)), col.numeric) : null}
          </Table.Td>
        ))}
    </Table.Tr>
  );
}
